import { clientUserText, entityLabel } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumber } from 'libphonenumber-js';
import api from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import { SECTION_LABELS, URL_SEGMENTS } from '../../../../config/Routesconfig';
import { adminSchema } from '../../../Utils/Validations';

// ─── Route-derived constants ───

const usersListPath = (username) =>
  `/${username}/${URL_SEGMENTS.dashboard}/${URL_SEGMENTS.users}`;

// ─── Component ────────────────
const EditUsers = () => {
  useTerminology();
  const SECTION_TITLE = SECTION_LABELS.users;
  const EDIT_LABEL = `Edit ${entityLabel('clientUser', 'singular', SECTION_LABELS.users.replace(/s$/, ''))}`;
  const { theme } = useContext(ThemeContext);

  const fontFamily =
    theme?.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const titleFontSize = theme?.typography?.titleFontSize || 24;
  const bodyFontSize = theme?.typography?.bodyFontSize || 14;
  const smallFontSize = Math.max(bodyFontSize - 2, 10);

  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    affiliate_code: '',
    country: 'United States',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone_number: '',
    email: '',
    permissions: [],
    password: '',
    password_confirmation: '',
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  // ── Countries / States ──
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);

  // ── Phone ──
  const [phoneError, setPhoneError] = useState('');

  // ── Duplicate warnings ──
  const [emailDupWarning, setEmailDupWarning] = useState(false);
  const [phoneDupWarning, setPhoneDupWarning] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);

  const navigate = useNavigate();
  const { username, id } = useParams();

  // ── Fetch all countries ──
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://countriesnow.space/api/v0.1/countries');
        const data = await res.json();
        if (data.error) throw new Error();
        setCountries(data.data.map((c) => c.country));
      } catch {
        setCountries(['United States', 'Canada', 'Other']);
      }
    })();
  }, []);

  // ── Fetch states when country changes ──
  useEffect(() => {
    if (!userData.country) {
      setStatesList([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: userData.country }),
        });
        const data = await res.json();
        setStatesList((data.data?.states || []).map((s) => s.name));
      } catch {
        setStatesList([]);
      }
    })();
  }, [userData.country]);

  // ── Fetch existing user data ──
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get(`/client-users/${id}`);
        setUserData({
          ...response.data.data,
          permissions: response.data.data.permissions || [],
          password: '',
          password_confirmation: '',
        });
        setFetching(false);
      } catch (error) {
        toast.error(
          'Failed to fetch user: ' + (error.response?.data?.message || error.message),
          { autoClose: 3000 }
        );
        navigate(usersListPath(username));
      }
    };
    fetchUser();
  }, [id, username, navigate]);

  // ── handleChange for text/select/checkbox fields ──
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const updatedData = {
      ...userData,
      [name]:
        type === 'checkbox' ? (checked ? ['administrator'] : []) : value,
    };

    // Reset state when country changes
    if (name === 'country') {
      updatedData.state = '';
    }

    setUserData(updatedData);

    // Reset duplicate warnings on email change
    if (name === 'email') {
      setEmailDupWarning(false);
      setEmailConfirmed(false);
    }

    const fieldsToValidate = ['email', 'password', 'password_confirmation'];
    if (fieldsToValidate.includes(name)) {
      try {
        adminSchema.validateSyncAt(name, updatedData);
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, [name]: err.message }));
      }
    } else {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // ── Phone change ──
  const handlePhoneChange = (value) => {
    const formatted = value.startsWith('+') ? value : `+${value}`;
    setUserData((prev) => ({ ...prev, phone_number: formatted }));
    if (phoneError) setPhoneError('');
    if (phoneDupWarning) {
      setPhoneDupWarning(false);
      setPhoneConfirmed(false);
    }
  };

  // ── Phone blur validation ──
  const handlePhoneBlur = () => {
    const val = userData.phone_number;
    if (!val || val === '+') {
      // phone is optional on edit — only validate format if something was entered
      setPhoneError('');
      return;
    }
    try {
      const parsed = parsePhoneNumber(val.startsWith('+') ? val : `+${val}`);
      if (!parsed?.isValid()) {
        setPhoneError('Invalid phone number');
        return;
      }
      setPhoneError('');
      handlePhoneDupCheck(val);
    } catch {
      setPhoneError('Invalid phone number');
    }
  };

  // ── Phone duplicate check ──
  const handlePhoneDupCheck = async (phoneVal) => {
    if (!phoneVal) return;
    try {
      const res = await api.post('/client-users/check-duplicate', {
        phone_number: phoneVal,
        exclude_id: id,
      });
      if (res.data.errors?.phone_number) {
        setPhoneDupWarning(true);
        setPhoneConfirmed(false);
      } else {
        setPhoneDupWarning(false);
      }
    } catch {
      setPhoneDupWarning(false);
    }
  };

  // ── Email blur duplicate check ──
  const handleEmailBlur = async () => {
    const emailVal = userData.email;
    if (!emailVal || errors.email) {
      setEmailDupWarning(false);
      return;
    }
    try {
      const res = await api.post('/client-users/check-duplicate', {
        email: emailVal,
        exclude_id: id,
      });
      if (res.data.errors?.email) {
        setEmailDupWarning(true);
        setEmailConfirmed(false);
      } else {
        setEmailDupWarning(false);
      }
    } catch {
      setEmailDupWarning(false);
    }
  };

  // ── Form validation ──
  const validateForm = async () => {
    try {
      await Promise.all([
        adminSchema.validateAt('email', userData),
        userData.password && adminSchema.validateAt('password', userData),
        userData.password && adminSchema.validateAt('password_confirmation', userData),
      ]);
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      if (err.path) newErrors[err.path] = err.message;
      setErrors(newErrors);
      return false;
    }
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone format if something was entered
    const phoneVal = userData.phone_number;
    if (phoneVal && phoneVal !== '+') {
      try {
        const parsed = parsePhoneNumber(phoneVal.startsWith('+') ? phoneVal : `+${phoneVal}`);
        if (!parsed?.isValid()) {
          setPhoneError('Invalid phone number');
          toast.error('Please fix the errors.');
          return;
        }
      } catch {
        setPhoneError('Invalid phone number');
        toast.error('Please fix the errors.');
        return;
      }
    }

    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fix the errors.');
      return;
    }

    // Block on unconfirmed duplicates
    if (emailDupWarning && !emailConfirmed) {
      toast.warning('Please confirm if you want to proceed with the existing email.');
      return;
    }
    if (phoneDupWarning && !phoneConfirmed) {
      toast.warning('Please confirm if you want to proceed with the existing phone number.');
      return;
    }

    setLoading(true);

    try {
      const payload = { ...userData };

      if (!userData.password) {
        delete payload.password;
        delete payload.password_confirmation;
      }

      payload.allow_duplicate_email = emailConfirmed ? 1 : 0;
      payload.allow_duplicate_phone = phoneConfirmed ? 1 : 0;

      await api.put(`/client-users/${id}`, payload);
      toast.success(clientUserText("User updated successfully."), { autoClose: 3000 });
      setTimeout(() => navigate(usersListPath(username)), 3000);

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update user.';
      toast.error(errorMessage, { autoClose: 3000 });
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(usersListPath(username));

  // ── Shared styles ──
  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    border: `1px solid ${hasError ? theme.colors.danger : theme.colors.borderColor}`,
    backgroundColor: theme.colors.bgPrimary,
    color: theme.colors.textPrimary,
  });

  const warningBoxStyle = {
    marginTop: '6px',
    padding: '8px 10px',
    borderRadius: '6px',
    background: '#FFF8E1',
    border: '1px solid #FFD54F',
    fontSize: '10px',
    color: '#7B5800',
  };

  const confirmBtnStyle = (color) => ({
    fontSize: '10px',
    padding: '3px 10px',
    borderRadius: '4px',
    background: color,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontFamily,
  });

  if (fetching) {
    return (
      <div style={{ fontFamily, fontSize: `${bodyFontSize}px`, color: theme.colors.textPrimary, textAlign: 'center', padding: '2rem' }}>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily,
        fontSize: `${bodyFontSize}px`,
        width: '100%',
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
      }}
    >
      <ToastContainer />

      {/* Phone input theme overrides */}
      <style>{`
        .users-phone .react-tel-input .form-control {
          width: 100%; height: 38px; padding: 6px 10px 6px 45px;
          font-size: ${bodyFontSize}px; font-family: inherit;
          color: ${theme.colors.textPrimary};
          background-color: ${theme.colors.bgPrimary};
          border: 1px solid ${theme.colors.borderColor};
          border-radius: 4px;
        }
        .users-phone .react-tel-input .form-control:focus {
          border-color: ${theme.colors.primary};
          box-shadow: 0 0 0 3px ${theme.colors.primary}25;
          outline: none;
        }
        .users-phone-error .react-tel-input .form-control {
          border-color: ${theme.colors.danger} !important;
        }
        .users-phone .react-tel-input .flag-dropdown {
          border: 1px solid ${theme.colors.borderColor};
          background-color: ${theme.colors.bgPrimary};
          border-right: none; border-radius: 4px 0 0 4px; height: 38px;
        }
        .users-phone .react-tel-input .country-list {
          background-color: ${theme.colors.bgPrimary};
          border: 1px solid ${theme.colors.borderColor};
          font-size: ${bodyFontSize}px;
          max-height: 200px; overflow-y: auto;
        }
        .users-phone .react-tel-input .country-list .country:hover {
          background-color: ${theme.colors.bgLight || '#f8f9fa'};
        }
        .users-phone .react-tel-input .country-list .country.highlight {
          background-color: ${theme.colors.primary}; color: #fff;
        }
      `}</style>

      {/* BREADCRUMB */}
      <div style={{ marginBottom: '1rem', fontSize: `${smallFontSize}px` }}>
        <Link
          to={usersListPath(username)}
          style={{ color: theme.colors.primary, textDecoration: 'underline' }}
        >
          {SECTION_TITLE}
        </Link>
        <span style={{ margin: '0 0.5rem' }}>&gt;</span>
        <span>{EDIT_LABEL}</span>
      </div>

      {/* WHITE CONTAINER */}
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '1rem',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          fontFamily,
          marginTop: '10px',
        }}
      >
        <h4
          style={{
            fontSize: `${titleFontSize}px`,
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: theme.colors.textPrimary,
          }}
        >
          {EDIT_LABEL}
        </h4>

        {/* FORM CONTAINER */}
        <div
          style={{
            padding: '1rem',
            borderRadius: '10px',
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            width: '100%',
            marginBottom: '1rem',
            boxSizing: 'border-box',
          }}
        >
          {/* NAME ROW */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>First Name*</label>
              <input
                type="text" name="first_name" value={userData.first_name}
                onChange={handleChange} style={inputStyle(errors.first_name)}
              />
              {errors.first_name && <span style={{ color: theme.colors.danger }}>{errors.first_name}</span>}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Last Name*</label>
              <input
                type="text" name="last_name" value={userData.last_name}
                onChange={handleChange} style={inputStyle(errors.last_name)}
              />
              {errors.last_name && <span style={{ color: theme.colors.danger }}>{errors.last_name}</span>}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Affiliate Code</label>
              <input
                type="text" name="affiliate_code" value={userData.affiliate_code || ''}
                onChange={handleChange} style={inputStyle(errors.affiliate_code)}
              />
              {errors.affiliate_code && <span style={{ color: theme.colors.danger }}>{errors.affiliate_code}</span>}
            </div>
          </div>

          {/* COUNTRY NOTE */}
          <div style={{ marginBottom: '1rem' }}>{clientUserText(" Please specify the country, city, and state where the user's office is located. ")}</div>

          {/* COUNTRY */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Country*</label>
              <select
                name="country" value={userData.country || ''} onChange={handleChange}
                style={{ ...inputStyle(errors.country), appearance: 'auto' }}
              >
                <option value="">Select Country</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.country && <span style={{ color: theme.colors.danger }}>{errors.country}</span>}
            </div>
          </div>

          {/* ADDRESS */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Address</label>
              <input
                type="text" name="address" value={userData.address || ''}
                onChange={handleChange} style={inputStyle(errors.address)}
              />
              {errors.address && <span style={{ color: theme.colors.danger }}>{errors.address}</span>}
            </div>
          </div>

          {/* CITY / STATE / ZIP */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>City*</label>
              <input
                type="text" name="city" value={userData.city}
                onChange={handleChange} style={inputStyle(errors.city)}
              />
              {errors.city && <span style={{ color: theme.colors.danger }}>{errors.city}</span>}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>State*</label>
              <select
                name="state" value={userData.state || ''} onChange={handleChange}
                disabled={!userData.country}
                style={{
                  ...inputStyle(errors.state),
                  appearance: 'auto',
                  opacity: !userData.country ? 0.6 : 1,
                }}
              >
                <option value="">Select Region</option>
                {statesList.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {errors.state && <span style={{ color: theme.colors.danger }}>{errors.state}</span>}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Zip*</label>
              <input
                type="text" name="zip" value={userData.zip}
                onChange={handleChange} style={inputStyle(errors.zip)}
              />
              {errors.zip && <span style={{ color: theme.colors.danger }}>{errors.zip}</span>}
            </div>
          </div>

          {/* PHONE NUMBER */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Phone Number</label>
              <div className={`users-phone${phoneError ? ' users-phone-error' : ''}`}>
                <PhoneInput
                  country={'us'}
                  value={userData.phone_number || ''}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  inputStyle={{
                    width: '100%',
                    height: '38px',
                    fontSize: `${bodyFontSize}px`,
                    fontFamily,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${phoneError ? theme.colors.danger : theme.colors.borderColor}`,
                    borderRadius: '4px',
                    color: theme.colors.textPrimary,
                    padding: '6px 10px 6px 45px',
                  }}
                  containerStyle={{ width: '100%' }}
                  buttonStyle={{
                    border: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: theme.colors.bgPrimary,
                    borderRight: 'none',
                    borderRadius: '4px 0 0 4px',
                    height: '38px',
                  }}
                />
              </div>

              {/* Format error */}
              {phoneError && (
                <span style={{ color: theme.colors.danger, fontSize: '12px' }}>{phoneError}</span>
              )}

              {/* Duplicate warning */}
              {!phoneError && phoneDupWarning && !phoneConfirmed && (
                <div style={warningBoxStyle}>
                  <div style={{ marginBottom: '6px' }}>{clientUserText(" This phone number is already used by another user. Do you want to proceed? ")}</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      style={confirmBtnStyle('#4CAF50')}
                      onClick={() => { setPhoneConfirmed(true); setPhoneDupWarning(false); }}
                    >
                      Yes, proceed
                    </button>
                    <button
                      type="button"
                      style={confirmBtnStyle('#f44336')}
                      onClick={() => { setPhoneDupWarning(false); setPhoneConfirmed(false); }}
                    >
                      No, change number
                    </button>
                  </div>
                </div>
              )}

              {/* Confirmed tick */}
              {!phoneError && phoneConfirmed && (
                <div style={{ fontSize: '12px', color: '#4CAF50', marginTop: '4px' }}>
                  ✓ Proceeding with existing phone number
                </div>
              )}
            </div>
          </div>

          {/* LOGIN CREDENTIALS */}
          <h5 style={{ fontSize: `${bodyFontSize}px`, fontWeight: 'bold', marginBottom: '1rem', color: theme.colors.textPrimary }}>
            Login Credentials
          </h5>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>

            {/* EMAIL */}
            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Email*</label>
              <input
                type="email" name="email" value={userData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                style={inputStyle(errors.email)}
              />
              {errors.email && <span style={{ color: theme.colors.danger }}>{errors.email}</span>}

              {/* Email duplicate warning */}
              {!errors.email && emailDupWarning && !emailConfirmed && (
                <div style={warningBoxStyle}>
                  <div style={{ marginBottom: '6px' }}>{clientUserText(" This email is already used by another user. Do you want to proceed? ")}</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      style={confirmBtnStyle('#4CAF50')}
                      onClick={() => { setEmailConfirmed(true); setEmailDupWarning(false); }}
                    >
                      Yes, proceed
                    </button>
                    <button
                      type="button"
                      style={confirmBtnStyle('#f44336')}
                      onClick={() => {
                        setEmailDupWarning(false);
                        setEmailConfirmed(false);
                        setUserData((prev) => ({ ...prev, email: '' }));
                      }}
                    >
                      No, change email
                    </button>
                  </div>
                </div>
              )}

              {/* Email confirmed tick */}
              {!errors.email && emailConfirmed && (
                <div style={{ fontSize: '12px', color: '#4CAF50', marginTop: '4px' }}>
                  ✓ Proceeding with existing email
                </div>
              )}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Password</label>
              <input
                type="password" name="password" value={userData.password || ''}
                onChange={handleChange} style={inputStyle(errors.password)}
              />
              {errors.password && <span style={{ color: theme.colors.danger }}>{errors.password}</span>}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Confirm Password</label>
              <input
                type="password" name="password_confirmation" value={userData.password_confirmation || ''}
                onChange={handleChange} style={inputStyle(errors.password_confirmation)}
              />
              {errors.password_confirmation && (
                <span style={{ color: theme.colors.danger }}>{errors.password_confirmation}</span>
              )}
            </div>

            <div style={{ flex: '0 0 32%', minWidth: '200px' }}>
              <label>Permissions</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox" name="permissions"
                  checked={userData.permissions.includes('administrator')}
                  onChange={handleChange}
                  style={{ width: '16px', height: '16px', accentColor: theme.colors.primary }}
                />
                <span style={{ marginLeft: '0.5rem' }}>Administrator</span>
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '0.5rem 1rem', fontSize: `${bodyFontSize}px`, borderRadius: '4px',
                border: 'none', background: theme.gradients.secondary,
                color: theme.colors.textWhite, cursor: 'pointer',
                transition: 'opacity 0.2s ease', fontFamily, outline: 'none',
              }}
              onMouseOver={(e) => (e.target.style.opacity = '0.9')}
              onMouseOut={(e) => (e.target.style.opacity = '1')}
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem', fontSize: `${bodyFontSize}px`, borderRadius: '4px',
                border: 'none', background: theme.gradients.primary,
                color: theme.colors.textWhite, cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s ease', opacity: loading ? 0.6 : 1,
                fontFamily, outline: 'none',
              }}
              onMouseOver={(e) => { if (!loading) e.target.style.opacity = '0.9'; }}
              onMouseOut={(e) => { if (!loading) e.target.style.opacity = '1'; }}
            >
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EditUsers;